import { TabButton, TabContainer } from '@shared/components/core/button/CommonButtonStyles';
import DeleteIconButton from '@shared/components/core/button/DeleteIconButton';
import TextButton from '@shared/components/core/button/TextButton';
import HorizontalFieldContainer from '@shared/components/core/field/HorizontalFieldContainer';
import ChipButtonContainer from '@shared/components/core/layouts/ChipButtonContainer';
import SuggestedPromptField from '@shared/components/input/field/SuggestedPromptField';
import UnsavedChangesDialog from '@shared/components/loading/UnsavedChangesDialog';
import EditProfileContainer from '@shared/hooks/state/EditProfileContainer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ProfileSuggestedPromptsPageContent() {
    const { t } = useTranslation();
    const editProfileContainer = EditProfileContainer.useContainer();
    const [activeTab, setActiveTab] = useState<'generate' | 'rewrite'>('generate');
    const [createButtonLabel, setCreateButtonLabel] = useState(t('common:suggestedPrompts.addGenerate'));
    const [placeholderText, setPlaceholderText] = useState(t('common:suggestedPrompts.generatePlaceholder'));
    const [prompts, setPrompts] = useState<string[]>(editProfileContainer.suggestedGeneratePrompts || []);

    useEffect(() => {
        if (activeTab === 'generate') {
            setCreateButtonLabel(t('common:suggestedPrompts.addGenerate'));
            setPlaceholderText(t('common:suggestedPrompts.generatePlaceholder'));
            setPrompts(editProfileContainer.suggestedGeneratePrompts || []);
        } else {
            setCreateButtonLabel(t('common:suggestedPrompts.addRewrite'));
            setPlaceholderText(t('common:suggestedPrompts.rewritePlaceholder'));
            setPrompts(editProfileContainer.suggestedRewritePrompts || []);
        }
    }, [activeTab]);

    const activeTabIsGenerate = activeTab === 'generate';

    const setUpdates = (prompts: string[]) => {
        if (activeTabIsGenerate) {
            editProfileContainer.updateSuggestedGeneratePrompts([...prompts]);
        } else {
            editProfileContainer.updateSuggestedRewritePrompts([...prompts]);
        }
        editProfileContainer.setHasChanges(true);
    };

    const updatePrompts = (value: string, index: number) => {
        prompts[index] = value;
        setUpdates(prompts);
    };

    const removePrompts = (index: number) => {
        prompts.splice(index, 1);
        setUpdates(prompts);
    };

    const addPrompts = () => {
        prompts.unshift('');
        setUpdates(prompts);
    };

    const handleSave = async () => {
        const result = await editProfileContainer.saveChanges();
    };
    return (
        <>
            <TabContainer role="tablist" aria-label="Profile Tabs">
                <TabButton
                    role="tab"
                    style={{ padding: '16px 32px' }}
                    aria-selected={activeTab === 'generate'}
                    onClick={() => setActiveTab('generate')}
                    isActive={activeTab === 'generate'}>
                    {t('common:suggestedPrompts.generate')}
                </TabButton>
                <TabButton
                    role="tab"
                    style={{ padding: '16px 32px' }}
                    aria-selected={activeTab === 'rewrite'}
                    onClick={() => setActiveTab('rewrite')}
                    isActive={activeTab === 'rewrite'}>
                    {t('common:suggestedPrompts.rewrite')}
                </TabButton>
            </TabContainer>

            {prompts.map((prompt, i) => (
                <HorizontalFieldContainer key={i}>
                    <SuggestedPromptField
                        value={prompt}
                        onChange={(value) => updatePrompts(value, i)}
                        disabled={editProfileContainer.isPreview}
                        placeholder={placeholderText}
                    />
                    <DeleteIconButton disabled={editProfileContainer.isPreview} onClick={() => removePrompts(i)}>
                        {t('common:suggestedPrompts.remove')}
                    </DeleteIconButton>
                </HorizontalFieldContainer>
            ))}
            <ChipButtonContainer>
                <TextButton $size="large" startIcon="plus" onClick={addPrompts}>
                    {createButtonLabel}
                </TextButton>
            </ChipButtonContainer>

            {editProfileContainer.hasChanges && (
                <UnsavedChangesDialog
                    hasChanges={editProfileContainer.hasChanges}
                    setHasChanges={editProfileContainer.setHasChanges}
                    onSave={handleSave}
                />
            )}
        </>
    );
}
